const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://127.0.0.1:3100';

export class ApiError extends Error {
  public override readonly name = 'ApiError';
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

type TokenGetter = () => string | null;
type RefreshFn = () => Promise<string | null>;

let _getToken: TokenGetter = () => null;
let _refresh: RefreshFn = async () => null;
let _refreshPromise: Promise<string | null> | null = null;
let _sessionRefreshPromise: Promise<Response> | null = null;

export function configureApiClient(getToken: TokenGetter, refresh: RefreshFn): void {
  _getToken = getToken;
  _refresh = refresh;
  _refreshPromise = null;
  _sessionRefreshPromise = null;
}

function refreshAccessToken(): Promise<string | null> {
  if (_refreshPromise) return _refreshPromise;
  const pending = _refresh().finally(() => {
    if (_refreshPromise === pending) _refreshPromise = null;
  });
  _refreshPromise = pending;
  return pending;
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as { message?: string };
    return new ApiError(res.status, body.message ?? res.statusText);
  } catch {
    return new ApiError(res.status, res.statusText);
  }
}

async function apiFetch(
  input: string,
  init: RequestInit = {},
  retry = true,
  tokenOverride?: string,
): Promise<Response> {
  const token = tokenOverride ?? _getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const fetchRequest = () =>
    fetch(`${API_BASE}${input}`, { ...init, headers, credentials: 'include' });
  let res: Response;
  if (input === '/auth/session/refresh') {
    if (!_sessionRefreshPromise) {
      const pending = fetchRequest().finally(() => {
        if (_sessionRefreshPromise === pending) _sessionRefreshPromise = null;
      });
      _sessionRefreshPromise = pending;
    }
    res = (await _sessionRefreshPromise).clone();
  } else {
    res = await fetchRequest();
  }

  // Never try to "refresh a refresh" — that recurses forever when there's no cookie.
  const establishesSession =
    input === '/auth/session/refresh' ||
    input === '/auth/magic-link/verify' ||
    input === '/auth/password/login' ||
    input === '/auth/password/register' ||
    input.startsWith('/auth/webauthn/login/');
  if (res.status === 401 && retry && !establishesSession) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return apiFetch(input, init, false, newToken);
    }
  }
  return res;
}

async function request<T>(input: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(input, init);
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export interface TokenPair {
  accessToken: string;
  refreshToken?: string;
  expiresInSec: number;
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  primaryHomeId: string | null;
}

export interface AuthResult extends TokenPair {
  user: AuthUser;
}

export const auth = {
  googleSignInUrl: () => `${API_BASE}/auth/google`,
  getPasskeyRegisterOptions: async (email: string, displayName?: string) =>
    (
      await request<{ options: Record<string, unknown> }>('/auth/webauthn/register/begin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, ...(displayName !== undefined ? { displayName } : {}) }),
      })
    ).options,

  verifyPasskeyRegister: (response: unknown) =>
    request<{ passkeyId: string }>('/auth/webauthn/register/finish', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ response }),
    }),

  getPasskeyLoginOptions: async () =>
    (
      await request<{ options: Record<string, unknown> }>('/auth/webauthn/login/begin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
    ).options,

  verifyPasskeyLogin: (response: unknown) =>
    request<AuthResult>('/auth/webauthn/login/finish', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ response }),
    }),

  requestMagicLink: (email: string) =>
    request<{ ok: boolean }>('/auth/magic-link/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }),

  consumeMagicLink: (token: string) =>
    request<AuthResult>('/auth/magic-link/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }),

  passwordRegister: (email: string, password: string, displayName?: string) =>
    request<AuthResult>('/auth/password/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, displayName }),
    }),

  passwordLogin: (email: string, password: string) =>
    request<AuthResult>('/auth/password/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    }),

  refresh: () => request<AuthResult>('/auth/session/refresh', { method: 'POST' }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<AuthUser>('/auth/me'),
  updateMe: (displayName: string | null) =>
    request<AuthUser>('/auth/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ displayName }),
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/auth/password/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  requestPasswordReset: (email: string) =>
    request<{ ok: true }>('/auth/password/reset/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }),
  confirmPasswordReset: (token: string, newPassword: string) =>
    request<AuthResult>('/auth/password/reset/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    }),
};

// ─── Homes ───────────────────────────────────────────────────────────────────

export interface Home {
  id: string;
  name: string;
  glyph: string | null;
  ownerUserId: string;
  role: string;
}

export const homes = {
  list: () => request<{ homes: Home[] }>('/homes'),
  create: (name: string, glyph?: string) =>
    request<Home>('/homes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, ...(glyph !== undefined ? { glyph } : {}) }),
    }),
  switchHome: (id: string) => request<{ ok: boolean }>(`/homes/${id}/switch`, { method: 'POST' }),
  invite: (id: string, email: string, role: string, message?: string) =>
    request<{ inviteId: string; expiresAt: string }>(`/homes/${id}/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role, ...(message !== undefined ? { message } : {}) }),
    }),
  audit: (id: string, limit = 50, before?: string) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (before) qs.set('before', before);
    return request<{ entries: AuditEntry[]; nextBefore: string | null }>(
      `/homes/${id}/audit?${qs.toString()}`,
    );
  },
  delete: (id: string) => request<void>(`/homes/${id}`, { method: 'DELETE' }),
};

export interface AuditEntry {
  id: string;
  action: string;
  actorUserId: string | null;
  actorEmail: string | null;
  actorDisplayName: string | null;
  target: string | null;
  success: boolean;
  createdAt: string;
}

// ─── Bridges ─────────────────────────────────────────────────────────────────

export interface Bridge {
  id: string;
  label: string;
  status: string;
  installPath: string | null;
  bridgeVersion: string | null;
  fingerprint: string | null;
  lastSeenAt: string | null;
  connected: boolean;
}

export const bridges = {
  list: (homeId: string) => request<{ bridges: Bridge[] }>(`/homes/${homeId}/bridges`),
  create: (homeId: string, label: string, installPath?: string) =>
    request<{ bridge: Bridge; pairingCode: string; pairingExpiresAt: string }>(
      `/homes/${homeId}/bridges`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, ...(installPath !== undefined ? { installPath } : {}) }),
      },
    ),
  newPairingCode: (homeId: string, bridgeId: string) =>
    request<{ pairingCode: string; pairingExpiresAt: string }>(
      `/homes/${homeId}/bridges/${bridgeId}/pairing-codes`,
      { method: 'POST' },
    ),
  sendCommand: (
    homeId: string,
    bridgeId: string,
    domain: string,
    service: string,
    entityId?: string,
    params?: Record<string, unknown>,
  ) =>
    request<{ ok: boolean; durationMs: number }>(`/homes/${homeId}/bridges/${bridgeId}/commands`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        domain,
        service,
        ...(entityId !== undefined ? { entityId } : {}),
        ...(params !== undefined ? { params } : {}),
      }),
    }),
  delete: (homeId: string, bridgeId: string) =>
    request<void>(`/homes/${homeId}/bridges/${bridgeId}`, { method: 'DELETE' }),
};

// ─── Bridge device-flow (activation) ─────────────────────────────────────────

export interface DeviceFlowSession {
  userCode: string;
  fingerprint: string;
  bridgeVersion: string;
  installPath: string | null;
  metadata: Record<string, unknown> | null;
  expiresAt: string;
  status: 'pending' | 'approved' | 'denied' | 'expired' | 'consumed';
}

export const deviceFlow = {
  lookup: (userCode: string) =>
    request<{ session: DeviceFlowSession }>(
      `/bridges/device/lookup?user_code=${encodeURIComponent(userCode)}`,
    ),
  approve: (userCode: string, homeId: string, label: string) =>
    request<{ bridgeId: string; bridgeLabel: string }>(`/bridges/device/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_code: userCode, home_id: homeId, label }),
    }),
  deny: (userCode: string) =>
    request<{ ok: boolean }>(`/bridges/device/deny`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_code: userCode }),
    }),
};

// ─── Devices ─────────────────────────────────────────────────────────────────

export interface EntityState {
  state: string;
  attributes: Record<string, unknown>;
  lastChanged?: string;
  lastUpdated?: string;
}

export const devices = {
  list: (homeId: string) =>
    request<{ homeId: string; entities: Record<string, EntityState> }>(`/homes/${homeId}/devices`),
};

// ─── Invites ─────────────────────────────────────────────────────────────────

export const invites = {
  get: (token: string) =>
    request<{
      homeId: string;
      homeName: string;
      inviterEmail: string;
      role: string;
      expiresAt: string;
    }>(`/invites/${token}`),
  accept: (token: string) =>
    request<{ homeId: string }>(`/invites/${token}/accept`, { method: 'POST' }),
};

// ─── Preferences ─────────────────────────────────────────────────────────────

export interface UserPrefs {
  theme: string;
  themeMode: string;
  density: string;
  locale: string;
  timezone: string;
  reducedMotion: boolean;
  highContrast: boolean;
  largeText: boolean;
  colorBlindMode: string;
}

export const prefs = {
  get: () => request<UserPrefs>('/me/preferences'),
  put: (p: Partial<UserPrefs>) =>
    request<UserPrefs>('/me/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(p),
    }),
};

// ─── Health (Phase 2) ────────────────────────────────────────────────────────

export interface BatteryEntry {
  entityId: string;
  name: string;
  room: string | null;
  percent: number;
  trendPercentPer30d: number | null;
  predictedDaysRemaining: number | null;
  lowConfidence: boolean;
  sampleCount: number;
  needsAttention: boolean;
  lastSampledAt: string | null;
}

export interface BatteriesResponse {
  homeId: string;
  generatedAt: string;
  attentionCount: number;
  batteries: BatteryEntry[];
}

export const health = {
  batteries: (homeId: string) =>
    request<BatteriesResponse>(`/homes/${homeId}/devices/health/batteries`),
  firmware: (homeId: string) =>
    request<FirmwareResponse>(`/homes/${homeId}/devices/health/firmware`),
  installFirmware: (homeId: string, entityId: string) =>
    request<{ ok: true; durationMs: number }>(`/homes/${homeId}/devices/health/firmware/install`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entityId }),
    }),
  connectivity: (homeId: string) =>
    request<ConnectivityResponse>(`/homes/${homeId}/devices/health/connectivity`),
};

export interface EventEntry {
  id: string;
  ts: string;
  entityId: string;
  entityName: string | null;
  domain: string;
  oldState: string | null;
  newState: string | null;
  attrsDelta: Record<string, unknown> | null;
  actorUserId: string | null;
  actorName: string | null;
  actorSource: 'user' | 'automation' | 'script' | 'unknown';
  correlationId: string | null;
  narrative: string | null;
  clusterSize: number;
}

export interface EventsResponse {
  homeId: string;
  events: EventEntry[];
  nextCursor: string | null;
}

export interface EventsQuery {
  from?: string;
  to?: string;
  entityId?: string;
  actorSource?: 'user' | 'automation' | 'script' | 'unknown';
  limit?: number;
  cursor?: string;
  expand?: 'correlated' | 'raw';
}

export interface StateAtEntity {
  entityId: string;
  entityName: string | null;
  domain: string;
  state: string | null;
  ts: string;
}

export interface StateAtResponse {
  homeId: string;
  ts: string;
  entities: StateAtEntity[];
}

export const events = {
  list: (homeId: string, query: EventsQuery = {}) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) params.set(k, String(v));
    }
    const qs = params.toString();
    return request<EventsResponse>(`/homes/${homeId}/events${qs ? `?${qs}` : ''}`);
  },
  cluster: (homeId: string, correlationId: string) =>
    request<{ events: EventEntry[] }>(`/homes/${homeId}/events/correlated/${correlationId}`),
  stateAt: (homeId: string, ts: string) =>
    request<StateAtResponse>(`/homes/${homeId}/state-at?ts=${encodeURIComponent(ts)}`),
};

export interface FirmwareEntry {
  entityId: string;
  bridgeId: string;
  title: string | null;
  currentVersion: string | null;
  latestVersion: string | null;
  releaseUrl: string | null;
  releaseSummary: string | null;
  severity: 'security' | 'feature' | 'unknown';
  updateAvailable: boolean;
  inProgress: boolean;
  lastSeenAt: string;
}

export interface FirmwareResponse {
  homeId: string;
  generatedAt: string;
  pendingCount: number;
  securityCount: number;
  firmware: FirmwareEntry[];
}

export interface ConnectivityEntry {
  entityId: string;
  name: string;
  protocol: 'zigbee' | 'zwave' | 'wifi' | 'thread' | 'unknown';
  silentMinutes: number;
  lastSignal: number | null;
  lastSignalUnit: string | null;
  needsAttention: boolean;
}

export interface ConnectivityResponse {
  homeId: string;
  generatedAt: string;
  flakiestEntities: ConnectivityEntry[];
  byProtocol: Record<string, number>;
}

export { apiFetch };
